import { FLAGS } from '@iheartjane/feature-flags';
import { Flex } from '@iheartjane/reefer';

import { CommunicationBanner } from '@jane/bloom/components/communicationBanner';
import { BloomCarousel } from '@jane/bloom/components/communicationBanner/carouselBanner/bloomCarousel';
import { CategorySelector } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/categorySelector';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useTypedRouteLoaderData } from '@jane/bloom/hooks/useTypedRouteLoaderData';
import type { CarouselBanner } from '@jane/bloom/types/schemas/roots/carouselBanner';
import type { CommunicationBannerViewModel } from '@jane/bloom/viewModels/communicationBanner';

import styles from './menuHeader.module.css';

export type MenuHeaderBanners = {
  communicationBanner: CommunicationBannerViewModel;
  desktopCarouselBanner: CarouselBanner;
  mobileCarouselBanner: CarouselBanner;
  showCarousel: boolean;
  showImageBanner: boolean;
  storeName?: string;
};

type MenuHeaderProps = {
  // Only the featured page has banner data; other PLP pages render tabs only.
  banners?: MenuHeaderBanners;
};

// Keeps tabs + carousel + comms banner in one place so upcoming menu-theme
// params can reorder them (carousel above tabs; prominent search above carousel).
export function MenuHeader({ banners }: MenuHeaderProps) {
  const { featureFlags, theme } = useRootLoaderData();
  const menuLayoutData = useTypedRouteLoaderData('routes/_menu');

  const useCustomComponents = featureFlags?.[FLAGS.c2CustomComponents] ?? false;
  const categoriesAboveCarousel = useCustomComponents
    ? (theme?.bloom?.category_tabs?.position ?? 'above_carousel') ===
      'above_carousel'
    : true;
  const displayCategories = menuLayoutData?.displayCategories;

  return (
    <Flex
      className={styles['container']}
      flexDirection="column"
      width="100%"
      gap={24}
    >
      {displayCategories && categoriesAboveCarousel && (
        <CategorySelector displayCategories={displayCategories} />
      )}
      {banners && (
        <>
          {!categoriesAboveCarousel && (
            <CommunicationBanner
              communicationBanner={banners.communicationBanner}
              showImageBanner={banners.showImageBanner}
            />
          )}
          {banners.showCarousel && banners.desktopCarouselBanner.length > 0 && (
            <div className="desktop-carousel-banner">
              <BloomCarousel
                items={banners.desktopCarouselBanner}
                storeName={banners.storeName}
              />
            </div>
          )}
          {banners.showCarousel && banners.mobileCarouselBanner.length > 0 && (
            <div className="mobile-carousel-banner">
              <BloomCarousel
                items={banners.mobileCarouselBanner}
                storeName={banners.storeName}
              />
            </div>
          )}
          {categoriesAboveCarousel && (
            <CommunicationBanner
              communicationBanner={banners.communicationBanner}
              showImageBanner={banners.showImageBanner}
            />
          )}
        </>
      )}
      {displayCategories && !categoriesAboveCarousel && (
        <CategorySelector displayCategories={displayCategories} />
      )}
    </Flex>
  );
}
